import { ComponentProps } from "react";
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  UseFormReturn,
  Path,
} from "react-hook-form";
import Root from "@shared/ui/Form";
import { FormField } from "./FormField";
import { TypeVariant } from "@shared/ui/Type";

export type FormComponent =
  | "Input"
  | "Textarea"
  | "Select"
  | "Checkbox"
  | "ForceYesInput"
  | "RadioGroup"
  | "YesNoButtons"
  | "OrgExperienceInputs"
  | "SelectChips"
  | "ResumeUploadInput"
  | "PhotoUploadInput"
  | "TimezoneSelect";

export interface FieldType<T extends FieldValues = FieldValues> {
  name: Path<T>;
  label: string;
  choices?: {
    value: number;
    label: string;
  }[];
  placeholder?: string;
  disabled?: boolean;
  component: FormComponent;
  helpText?: string;
  required?: boolean;
  icon?: React.ReactNode;
  prefix?: string;
  transformOnChange?: (val: string) => string;
  image?: string;
  labelVariant?: TypeVariant;
  isHideErrorMessage?: boolean;
  errorContainerClassName?: string;
  type?: string;
  layout?: "vertical" | "horizontal";
  autoComplete?: "current-password" | "new-password";
}

interface Props<T extends FieldValues>
  extends Omit<ComponentProps<"form">, "onSubmit"> {
  form: UseFormReturn<T>;
  fields: FieldType<T>[];
  children?: React.ReactNode;
  onSubmit?: SubmitHandler<T>;
  className?: string;
  innerClassName?: string;
  formFieldClassName?: string;
  errorContainerClassName?: string;
  isSubmitting?: boolean;
}

export const Form = <T extends FieldValues>({
  children,
  className,
  form,
  fields,
  onSubmit,
  innerClassName,
  isSubmitting: isSubmittingProp = false,
  formFieldClassName,
  errorContainerClassName,
  ...props
}: Props<T>) => {
  const { isLoading, isSubmitting, errors } = form.formState;

  return (
    <FormProvider {...form}>
      <Root
        className={className}
        innerClassName={innerClassName}
        onSubmit={onSubmit ? form.handleSubmit(onSubmit) : undefined}
        isLoading={isLoading}
        isSubmitting={isSubmitting || isSubmittingProp}
        errors={errors}
        errorContainerClassName={errorContainerClassName}
        {...props}
      >
        <>
          {fields.map((field) => {
            return (
              <FormField
                key={field.name}
                id={field.name}
                className={formFieldClassName}
                {...field}
              />
            );
          })}
          {children}
        </>
      </Root>
    </FormProvider>
  );
};

export default Form;
